import { useEffect, useState } from "react";
import { TrendingUp, Building2 } from "lucide-react";
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import { api } from "../../lib/api";

const COLORS = ["#1B2F6E", "#F5A623", "#10B981", "#3B82F6", "#EF4444"];

export default function Estadisticas() {
    const [stats, setStats] = useState(null);
    const [charts, setCharts] = useState(null);
    const [schools, setSchools] = useState([]);
    const [schoolFilter, setSchoolFilter] = useState("");

    useEffect(() => {
        api.get("/ejecutivo/stats").then((r) => setStats(r.data)).catch(() => {});
        api.get("/ejecutivo/schools").then((r) => setSchools(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const params = schoolFilter ? { school: schoolFilter } : {};
        api.get("/ejecutivo/charts", { params }).then((r) => setCharts(r.data)).catch(() => {});
    }, [schoolFilter]);

    if (!stats || !charts) return <div className="p-6 text-edu-muted">Cargando…</div>;

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="estadisticas-page">
            <h1 className="text-3xl font-semibold mb-1 inline-flex items-center gap-2">
                <TrendingUp size={28} className="text-edu-navy" /> Estadísticas
            </h1>
            <p className="text-edu-body mb-5">Métricas globales de la plataforma.</p>

            <div className="mb-7">
                <label className="text-[10px] uppercase tracking-widest text-edu-muted font-semibold mb-1 block inline-flex items-center gap-1">
                    <Building2 size={11} /> Filtrar por colegio
                </label>
                <select
                    className="edu-input min-w-[260px]"
                    value={schoolFilter}
                    onChange={(e) => setSchoolFilter(e.target.value)}
                    data-testid="filter-school"
                >
                    <option value="">Todos los colegios</option>
                    {schools.map((s) => (
                        <option key={s.id} value={s.name}>{s.name}</option>
                    ))}
                </select>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
                <Card label="Colegios activos" value={stats.total_schools} />
                <Card label="Estudiantes activos" value={stats.total_students} />
                <Card label="Profesores activos" value={stats.total_teachers} />
                <Card label="Sesiones IA hoy" value={stats.ai_sessions_today} />
                <Card label="Monedas consumidas" value={stats.coins_consumed_total} gold />
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <ChartCard title="Estudiantes activos por colegio">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={charts.students_by_school}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="school" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="students" fill="#1B2F6E" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Monedas consumidas por colegio">
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={charts.coins_by_school}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="school" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Bar dataKey="coins" fill="#F5A623" radius={[6, 6, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Sesiones IA en el tiempo">
                    <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={charts.ai_over_time}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                            <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Line
                                type="monotone"
                                dataKey="sessions"
                                stroke="#1B2F6E"
                                strokeWidth={3}
                                dot={{ fill: "#F5A623", r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Funcionalidades más usadas">
                    <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                            <Pie
                                data={charts.features}
                                dataKey="count"
                                nameKey="feature"
                                cx="50%"
                                cy="50%"
                                outerRadius={90}
                                label
                            >
                                {charts.features.map((_, i) => (
                                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>
        </div>
    );
}

function Card({ label, value, gold }) {
    return (
        <div className={`${gold ? "bg-edu-gold text-[#0A0A0A]" : "edu-card"} p-6`}>
            <div className="text-xs uppercase tracking-widest font-semibold opacity-70">{label}</div>
            <div className="text-4xl font-bold mt-1">{value}</div>
        </div>
    );
}

function ChartCard({ title, children }) {
    return (
        <div className="edu-card p-5">
            <h3 className="text-sm font-semibold text-edu-navy mb-3">{title}</h3>
            {children}
        </div>
    );
}
